// src/pages/UpgradePage.jsx
// $29/month "AI Implementation OS" subscription upgrade page
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import {
  CheckCircle, Zap, RefreshCw, Mail, TrendingUp, BarChart2,
  ArrowRight, Clock, X, Shield,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://ai-workshop-llc-backend.onrender.com";

const FEATURES = [
  {
    icon: RefreshCw,
    title: "Auto-updating tool recommendations",
    desc: "As new AI tools launch and rankings shift, your blueprint refreshes automatically. No more stale advice.",
    color: "text-[#3b82f6]", bg: "bg-blue-500/10", border: "border-blue-500/20",
  },
  {
    icon: Mail,
    title: "Weekly AI industry digest",
    desc: "Every Monday: top 3 new AI tools for your industry, what's changed, and one actionable tip. Curated from live data.",
    color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20",
  },
  {
    icon: TrendingUp,
    title: "Progress tracking persistence",
    desc: "Your 12-week checklist and playbook completion syncs across devices and is never lost. See exactly where you are.",
    color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20",
  },
  {
    icon: BarChart2,
    title: "Updated ROI benchmarks",
    desc: "Your ROI projections recalibrate as we collect real outcomes from businesses in your industry. Numbers stay accurate.",
    color: "text-[#3b82f6]", bg: "bg-blue-500/10", border: "border-blue-500/20",
  },
  {
    icon: Zap,
    title: "Priority Ask AI responses",
    desc: "Your Q&A sessions get priority processing. Ask anything about your implementation and get answers in seconds.",
    color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/20",
  },
  {
    icon: Shield,
    title: "New tools added weekly",
    desc: "Our database grows weekly. Subscribers get access to new tool recommendations the moment they're vetted and scored.",
    color: "text-indigo-400", bg: "bg-indigo-500/10", border: "border-indigo-500/20",
  },
];

const COMPARE = [
  { feature: "AI blueprint (one-time)", oneTime: true, sub: true },
  { feature: "Tool recommendations", oneTime: "Static snapshot", sub: "Updated weekly" },
  { feature: "ROI projections", oneTime: "At purchase", sub: "Recalibrated monthly" },
  { feature: "Weekly industry digest", oneTime: false, sub: true },
  { feature: "Progress sync across devices", oneTime: "localStorage only", sub: "Cloud-synced" },
  { feature: "New tools as they launch", oneTime: false, sub: true },
  { feature: "Updated benchmarks", oneTime: false, sub: true },
];

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

const UpgradePage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/create-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          originUrl: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create checkout");
      window.location.href = data.checkoutUrl;
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <Helmet>
        <title>AI Implementation OS — $29/month | AI Workshop LLC</title>
        <meta name="description" content="Your AI blueprint, always up to date. Auto-updating tool recommendations, weekly industry digest, and progress tracking that never goes stale." />
      </Helmet>
      <Header />

      {/* HERO */}
      <section className="relative pt-20 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-teal-600/10 rounded-full filter blur-[120px]" />
          <div className="absolute bottom-0 right-1/3 w-[500px] h-[500px] bg-blue-600/10 rounded-full filter blur-[120px]" />
        </div>
        <div className="max-w-4xl mx-auto relative text-center">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 text-[#3b82f6] text-sm font-semibold px-4 py-2 rounded-full mb-8">
              <RefreshCw className="w-4 h-4" /> Your blueprint, always current
            </div>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
            <span className="text-white">The AI Landscape Changes.</span>
            <br />
            <span className="bg-gradient-to-r from-teal-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">Your Blueprint Should Too.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-lg text-slate-400 mb-10 leading-relaxed max-w-2xl mx-auto">
            A one-time blueprint tells you what to do today. The <strong className="text-white">AI Implementation OS</strong> keeps your recommendations updated as new tools launch, benchmarks change, and your industry evolves.
          </motion.p>

          {/* Pricing card */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="max-w-md mx-auto bg-slate-900/70 backdrop-blur-xl border border-blue-500/30 rounded-3xl p-8 shadow-2xl shadow-blue-500/10 mb-6">
            <div className="flex items-baseline justify-center gap-2 mb-2">
              <span className="text-5xl font-black text-white">$29</span>
              <span className="text-slate-400 text-lg">/month</span>
            </div>
            <p className="text-slate-500 text-sm mb-6">Cancel anytime. No long-term commitment.</p>

            <div className="space-y-3 mb-8 text-left">
              {[
                "Auto-updating tool recommendations",
                "Weekly AI digest for your industry",
                "Cloud-synced progress tracking",
                "Updated ROI benchmarks monthly",
                "All new tools as they're vetted",
              ].map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-[#3b82f6] flex-shrink-0" />
                  <span className="text-slate-300 text-sm">{f}</span>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                className="w-full bg-slate-950/50 border border-white/15 text-white placeholder-slate-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors"
              />
              <Button
                onClick={handleSubscribe}
                disabled={loading}
                className="w-full bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:shadow-blue-500/30 text-slate-950 font-black text-base py-4 rounded-full shadow-lg transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60"
              >
                {loading ? "Redirecting..." : <>Start My Subscription <ArrowRight className="w-4 h-4 ml-2 inline" /></>}
              </Button>
            </div>

            <div className="flex items-center justify-center gap-4 mt-5">
              {[{ icon: Clock, text: "Cancel anytime" }, { icon: Shield, text: "Stripe-secured" }].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-1.5 text-slate-500 text-xs">
                  <Icon className="w-3 h-3" /><span>{text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-slate-600 text-sm">
            Already got the $149 blueprint? This keeps it alive.
          </motion.p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 px-4 border-t border-white/8">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <div className="text-sm text-[#3b82f6] font-semibold tracking-widest uppercase mb-3">What's included</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">Everything That Keeps Your Plan Current</h2>
              <p className="text-slate-400 max-w-xl mx-auto">The AI tool landscape shifts every week. Your blueprint should too.</p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {FEATURES.map((f) => (
                <motion.div key={f.title} variants={fadeUp} className={`rounded-2xl border p-6 ${f.bg} ${f.border}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${f.bg} border ${f.border} mb-4`}>
                    <f.icon className={`w-5 h-5 ${f.color}`} />
                  </div>
                  <h3 className="text-white font-bold text-base mb-2">{f.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-20 px-4 border-t border-white/8 bg-slate-900/30">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <div className="text-center mb-12">
              <div className="text-sm text-purple-400 font-semibold tracking-widest uppercase mb-3">Comparison</div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">One-Time Blueprint vs. Implementation OS</h2>
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <div className="grid grid-cols-3 bg-slate-900/70 px-5 py-4">
                <div className="text-slate-400 text-sm font-semibold">Feature</div>
                <div className="text-center text-slate-400 text-sm font-semibold">$149 Blueprint</div>
                <div className="text-center text-[#3b82f6] text-sm font-semibold">+ $29/mo OS</div>
              </div>
              {COMPARE.map((row, i) => (
                <div key={row.feature} className={`grid grid-cols-3 px-5 py-3.5 items-center ${i % 2 === 0 ? "bg-slate-900/40" : "bg-slate-900/20"}`}>
                  <span className="text-slate-300 text-sm">{row.feature}</span>
                  <div className="flex justify-center">
                    {row.oneTime === true
                      ? <CheckCircle className="w-4 h-4 text-[#3b82f6]" />
                      : row.oneTime === false
                      ? <X className="w-4 h-4 text-slate-600" />
                      : <span className="text-slate-500 text-xs text-center">{row.oneTime}</span>}
                  </div>
                  <div className="flex justify-center">
                    {row.sub === true
                      ? <CheckCircle className="w-4 h-4 text-[#3b82f6]" />
                      : row.sub === false
                      ? <X className="w-4 h-4 text-slate-600" />
                      : <span className="text-[#3b82f6] text-xs font-semibold text-center">{row.sub}</span>}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-24 px-4 border-t border-white/8">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
              AI moves fast.
              <br />
              <span className="bg-gradient-to-r from-teal-400 to-blue-400 bg-clip-text text-transparent">Don't get left behind.</span>
            </h2>
            <p className="text-slate-400 text-lg mb-10 leading-relaxed">
              $29/month. Cancel anytime. Your blueprint stays current for as long as you're subscribed.
            </p>
            <div className="max-w-sm mx-auto space-y-3">
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                className="w-full bg-slate-950/50 border border-white/15 text-white placeholder-slate-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors"
              />
              <Button
                onClick={handleSubscribe}
                disabled={loading}
                className="w-full bg-gradient-to-br from-[#3b82f6] to-blue-500 hover:shadow-blue-500/30 text-slate-950 font-black text-lg py-5 rounded-full shadow-xl transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60"
              >
                {loading ? "Redirecting..." : <>Start My Subscription — $29/mo <ArrowRight className="w-5 h-5 ml-2 inline" /></>}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UpgradePage;
